import { useEffect, useState } from 'react';

/**
 * PWA install helper for the patient portal.
 *
 * - Injects `<link rel="manifest">`, `theme-color` and apple-mobile-web-app
 *   tags into <head> while the portal is mounted. Cleans up on unmount so
 *   the staff app (mounted at `/*`) is unaffected.
 * - Registers `/portal-sw.js` (scope `/portal`) once per session.
 * - Captures the `beforeinstallprompt` event so PortalLayout can render an
 *   "Install app" chip.
 */

const HEAD_TAGS = [
  { tag: 'link', attrs: { rel: 'manifest', href: '/portal-manifest.webmanifest' } },
  { tag: 'meta', attrs: { name: 'theme-color', content: '#059669' } },
  { tag: 'meta', attrs: { name: 'viewport', content: 'width=device-width, initial-scale=1, viewport-fit=cover, interactive-widget=resizes-content' } },
  { tag: 'meta', attrs: { name: 'apple-mobile-web-app-capable', content: 'yes' } },
  { tag: 'meta', attrs: { name: 'apple-mobile-web-app-title', content: 'DutiLyne' } },
  { tag: 'meta', attrs: { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' } },
  { tag: 'link', attrs: { rel: 'apple-touch-icon', sizes: '180x180', href: '/portal-icon-180.png?v=3' } },
  // iOS launch image. One portrait asset is offered for the common iPhone
  // classes; Android/Chromium derives its splash from the manifest icon +
  // background_color instead.
  { tag: 'link', attrs: { rel: 'apple-touch-startup-image', href: '/portal-splash.jpg' } },
  { tag: 'link', attrs: { rel: 'apple-touch-startup-image', href: '/portal-splash.jpg',
    media: '(device-width: 430px) and (device-height: 932px) and (orientation: portrait)' } },
  { tag: 'link', attrs: { rel: 'apple-touch-startup-image', href: '/portal-splash.jpg',
    media: '(device-width: 393px) and (device-height: 852px) and (orientation: portrait)' } },
  { tag: 'link', attrs: { rel: 'apple-touch-startup-image', href: '/portal-splash.jpg',
    media: '(device-width: 390px) and (device-height: 844px) and (orientation: portrait)' } },
  { tag: 'link', attrs: { rel: 'apple-touch-startup-image', href: '/portal-splash.jpg',
    media: '(device-width: 375px) and (device-height: 812px) and (orientation: portrait)' } },
];

const MARK = 'data-portal-pwa';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Meta tags where the *name* is unique (only one should exist on the page).
// If a pre-existing tag lives in index.html we snapshot its value, override
// it with the portal's value while mounted, and restore on unmount.
const OVERRIDE_META = new Set(['viewport', 'theme-color', 'apple-mobile-web-app-capable', 'apple-mobile-web-app-title', 'apple-mobile-web-app-status-bar-style']);

export function installHeadTags() {
  const previousTitle = document.title;
  const created = [];
  const overrides = [];  // {el, prevContent}

  HEAD_TAGS.forEach(({ tag, attrs }) => {
    if (tag === 'meta' && OVERRIDE_META.has(attrs.name)) {
      const existing = document.querySelector(`meta[name="${attrs.name}"]`);
      if (existing) {
        overrides.push({ el: existing, prevContent: existing.getAttribute('content') });
        existing.setAttribute('content', attrs.content);
        return;
      }
    }
    const el = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    el.setAttribute(MARK, '1');
    document.head.appendChild(el);
    created.push(el);
  });

  return () => {
    created.forEach((el) => el.remove());
    overrides.forEach(({ el, prevContent }) => {
      if (prevContent === null) el.removeAttribute('content');
      else el.setAttribute('content', prevContent);
    });
    document.title = previousTitle;
  };
}

export function updatePortalHead(pharmacy, branch = null) {
  const manifest = document.querySelector(`link[rel="manifest"][${MARK}]`);
  const theme = document.querySelector('meta[name="theme-color"]');
  const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  const touchIcon = document.querySelector(`link[rel="apple-touch-icon"][${MARK}]`);
  const version = encodeURIComponent(pharmacy?.icon_version || '2');
  if (manifest) manifest.setAttribute('href', pharmacy?.slug
    ? `${BACKEND_URL}/api/portal/manifest/${pharmacy.slug}.webmanifest?v=${version}${branch?.slug ? `&branch_slug=${encodeURIComponent(branch.slug)}` : ''}` : '/portal-manifest.webmanifest');
  if (theme) theme.setAttribute('content', pharmacy?.primary_color || '#059669');
  // Home-screen label stays the app name; the pharmacy name lives in the title bar.
  if (appleTitle) appleTitle.setAttribute('content', 'DutiLyne');
  if (touchIcon) touchIcon.setAttribute('href', pharmacy?.slug
    ? `${BACKEND_URL}/api/portal/manifest/${pharmacy.slug}/icon/180.png?purpose=any&v=${version}` : '/portal-icon-180.png?v=3');
  document.title = `${pharmacy?.name || 'DutiLyne'} · Patient Portal`;
}

let swRegistered = false;
export async function registerPortalServiceWorker() {
  if (swRegistered) return;
  if (!('serviceWorker' in navigator)) return;
  try {
    await navigator.serviceWorker.register('/portal-sw.js', { scope: '/portal' });
    swRegistered = true;
  } catch (err) {
    // Fail silently — PWA install still works with just a manifest in
    // most modern browsers, and dev environments may not allow SW registration.
    console.warn('Portal SW registration failed:', err?.message || err);
  }
}

/**
 * Subscribe to the `beforeinstallprompt` event and return a `promptInstall`
 * function that the UI can trigger from a click handler. Also exposes the
 * `deferredPrompt` so callers can hide the install chip once accepted.
 */
let pendingInstall = null;
let appInstalled = false;
const installSubscribers = new Set();
const notifyInstall = () => installSubscribers.forEach(notify => notify());
const installedMode = () => appInstalled || window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
async function promptInstall() {
  const event = pendingInstall;
  if (!event || installedMode()) return false;
  pendingInstall = null;
  try {
    await event.prompt();
    const result = await event.userChoice;
    if (result.outcome === 'accepted') appInstalled = true;
    return result.outcome === 'accepted';
  } catch { return false; }
  finally { notifyInstall(); }
}
// Capture once at portal module load, not only after an onboarding offer mounts.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', event => { event.preventDefault(); pendingInstall = event; notifyInstall(); });
  window.addEventListener('appinstalled', () => { appInstalled = true; pendingInstall = null; notifyInstall(); });
}
export function watchInstallPrompt(onAvailable, onInstalled) {
  const update = () => {
    if (installedMode()) { onAvailable(null); onInstalled?.(); }
    else onAvailable(pendingInstall ? promptInstall : null);
  };
  installSubscribers.add(update);
  update();
  return () => installSubscribers.delete(update);
}

/** Shared install-prompt state — Account page offers it as a single optional row. */
export function usePortalInstallPrompt() {
  const [installTrigger, setInstallTrigger] = useState(null);
  const [isInstalled, setIsInstalled] = useState(installedMode);
  useEffect(() => watchInstallPrompt((trigger) => setInstallTrigger(() => trigger), () => { setInstallTrigger(null); setIsInstalled(true); }), []);
  const install = async () => installTrigger ? installTrigger() : false;
  return { canInstall: !!installTrigger && !isInstalled, install, isInstalled };
}
